import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ExternalLink, LogOut } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RsvpDashboard } from "@/components/wedding/RsvpDashboard";
import { ContentEditor } from "@/components/wedding/ContentEditor";
import { ChangePassword } from "@/components/wedding/ChangePassword";
import { ModerationPanel } from "@/components/wedding/ModerationPanel";
import { ThemeToggle } from "@/components/wedding/ThemeToggle";
import { Login } from "@/components/wedding/Login";
import { getContent, getMe, getToken, clearToken, listGuestbook, approveGuestbook, deleteGuestbookEntry, listSuggestions, approveSuggestion, deleteSuggestion } from "@/lib/api";

export default function Cms() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [content, setContent] = useState(null);

  const check = () => {
    if (!getToken()) { setAuthed(false); return; }
    getMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  };

  useEffect(() => { check(); }, []);
  useEffect(() => {
    if (authed) getContent(true).then(setContent).catch(() => {});
  }, [authed]);

  const logout = () => { clearToken(); setAuthed(false); };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[var(--surface)] text-[var(--gold)] font-serif italic text-xl">Loading…</div>;
  }
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[var(--surface)]" data-testid="cms-page">
      <header className="bg-[var(--card)] border-b border-[var(--border)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[var(--gold)]" />
            <div>
              <p className="font-serif text-lg text-[var(--burgundy)] leading-none">Wedding CMS</p>
              <p className="text-xs text-[var(--text-muted)]">
                {content ? `${content.couple?.partner1} & ${content.couple?.partner2}` : "Loading..."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-[var(--gold-hover)] flex items-center gap-1.5" data-testid="cms-view-site">
              View site <ExternalLink className="w-4 h-4" />
            </Link>
            <ThemeToggle style={{ color: "var(--text-secondary)" }} />
            <button onClick={logout} className="text-sm text-[var(--text-secondary)] flex items-center gap-1.5 hover:text-[var(--burgundy)]" data-testid="cms-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <Tabs defaultValue="rsvps">
          <TabsList className="mb-8 inline-flex flex-wrap h-auto w-full gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-1.5 sm:w-auto" data-testid="cms-tabs">
            <TabsTrigger value="rsvps" data-testid="cms-tab-rsvps" className="flex-1 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition-all data-[state=active]:bg-[var(--gold)] data-[state=active]:text-white data-[state=active]:shadow sm:flex-none">RSVPs &amp; Stay Planner</TabsTrigger>
            <TabsTrigger value="content" data-testid="cms-tab-content-editor" className="flex-1 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition-all data-[state=active]:bg-[var(--gold)] data-[state=active]:text-white data-[state=active]:shadow sm:flex-none">Site Content</TabsTrigger>
            <TabsTrigger value="settings" data-testid="cms-tab-settings" className="flex-1 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition-all data-[state=active]:bg-[var(--gold)] data-[state=active]:text-white data-[state=active]:shadow sm:flex-none">Settings</TabsTrigger>
            <TabsTrigger value="guestbook" data-testid="cms-tab-guestbook" className="flex-1 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition-all data-[state=active]:bg-[var(--gold)] data-[state=active]:text-white data-[state=active]:shadow sm:flex-none">Guestbook</TabsTrigger>
            <TabsTrigger value="ideas" data-testid="cms-tab-ideas" className="flex-1 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition-all data-[state=active]:bg-[var(--gold)] data-[state=active]:text-white data-[state=active]:shadow sm:flex-none">Community Ideas</TabsTrigger>
          </TabsList>
          <TabsContent value="rsvps">
            <RsvpDashboard />
          </TabsContent>
          <TabsContent value="content">
            {content && <ContentEditor content={content} onSaved={setContent} />}
          </TabsContent>
          <TabsContent value="settings">
            <ChangePassword />
          </TabsContent>
          <TabsContent value="guestbook">
            <ModerationPanel
              testid="cms-guestbook-panel"
              emptyText="No wishes yet. As guests leave blessings, they'll appear here."
              load={listGuestbook} approve={approveGuestbook} remove={deleteGuestbookEntry}
              renderItem={(it) => (<><p className="text-[var(--text-secondary)] whitespace-pre-line leading-relaxed">{it.message}</p><p className="mt-2 font-serif text-lg text-[var(--burgundy)]">— {it.name}</p></>)}
            />
          </TabsContent>
          <TabsContent value="ideas">
            <ModerationPanel
              testid="cms-suggestions-panel"
              emptyText="No ideas yet. Guest suggestions will show up here."
              load={listSuggestions} approve={approveSuggestion} remove={deleteSuggestion}
              renderItem={(it) => (<><p className="font-medium text-[var(--text-primary)]">{it.activity}</p>{it.note && <p className="text-sm text-[var(--text-secondary)] mt-0.5">{it.note}</p>}<p className="mt-1.5 text-sm font-serif text-[var(--burgundy)]">— {it.name}</p></>)}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
